'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { EditableContent } from '../../../edit/EditableContent';
import { EditableMedia } from '../../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../../lib/responsiveImageSizes';
import { pickChrome } from '../../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName } from '../MenuItemTags';
import { isMenuItemActive, resolveItemImageUrl } from '../menuEditHelpers';
import { readHighlightMenuSection } from '../highlight/highlightMenuItems';

function nearestPlateIndex(rail: HTMLDivElement, plates: HTMLElement[]): number {
  const origin = rail.getBoundingClientRect().left;
  let best = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < plates.length; i += 1) {
    const dist = Math.abs(plates[i]!.getBoundingClientRect().left - origin);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

/**
 * Homepage highlight — cinematic dish promenade.
 * Native snap-scroll rail; theme-radius slide marks under plates from the second item.
 */
export function MenuHighlightStrip({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const { sectionTitle, sectionSubtitle, dishes } = readHighlightMenuSection(
    block,
    locale,
  );
  const target = isActive ? activeEditTarget : null;
  const railRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canOverflow, setCanOverflow] = useState(false);
  const showMarks = dishes.length > 1 && !editable;
  const marksNavLabel = pickChrome('highlightPromenadeNav', locale);
  const goToDishLabel = pickChrome('highlightPromenadeGoTo', locale);

  const syncRail = useCallback(() => {
    const rail = railRef.current;
    if (!rail) {
      setCanOverflow(false);
      setActiveIndex(0);
      return;
    }
    const plates = [...rail.querySelectorAll<HTMLElement>('.ws-menu-highlight__plate')];
    setCanOverflow(rail.scrollWidth > rail.clientWidth + 2);
    if (plates.length > 0) {
      setActiveIndex(nearestPlateIndex(rail, plates));
    }
  }, []);

  const scrollToIndex = useCallback((index: number) => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }
    const plates = [...rail.querySelectorAll<HTMLElement>('.ws-menu-highlight__plate')];
    const plate = plates[index];
    if (!plate) {
      return;
    }
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    plate.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      inline: 'start',
      block: 'nearest',
    });
    setActiveIndex(index);
  }, []);

  const onBrowseKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (editable || dishes.length < 2) {
        return;
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }
      const rail = railRef.current;
      if (!rail) {
        return;
      }
      const plates = [...rail.querySelectorAll<HTMLElement>('.ws-menu-highlight__plate')];
      if (plates.length < 2) {
        return;
      }
      const current = nearestPlateIndex(rail, plates);
      const next =
        event.key === 'ArrowRight'
          ? Math.min(plates.length - 1, current + 1)
          : Math.max(0, current - 1);
      if (next === current) {
        return;
      }
      event.preventDefault();
      scrollToIndex(next);
    },
    [dishes.length, editable, scrollToIndex],
  );

  useEffect(() => {
    const rail = railRef.current;
    if (!rail || dishes.length < 2) {
      return;
    }
    syncRail();
    let ticking = false;
    const onScroll = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        syncRail();
      });
    };

    /** Mouse/pen drag — touch keeps native pan. Skip in edit so plates stay selectable. */
    const DRAG_THRESHOLD_PX = 4;
    let dragging = false;
    let moved = false;
    let pointerId: number | null = null;
    let startX = 0;
    let startScroll = 0;
    let suppressClick = false;

    const endDrag = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      rail.classList.remove('ws-menu-highlight__rail--dragging');
      if (pointerId != null && rail.hasPointerCapture(pointerId)) {
        try {
          rail.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }
      }
      pointerId = null;
      if (moved) {
        suppressClick = true;
        window.setTimeout(() => {
          suppressClick = false;
        }, 0);
      }
      moved = false;
      syncRail();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (editable || event.pointerType === 'touch' || event.button !== 0) {
        return;
      }
      if (rail.scrollWidth <= rail.clientWidth + 2) {
        return;
      }
      event.preventDefault();
      dragging = true;
      moved = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
      rail.classList.add('ws-menu-highlight__rail--dragging');
      rail.setPointerCapture(event.pointerId);
      window.getSelection()?.removeAllRanges();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!dragging || event.pointerId !== pointerId) {
        return;
      }
      const dx = event.clientX - startX;
      if (Math.abs(dx) >= DRAG_THRESHOLD_PX) {
        moved = true;
        event.preventDefault();
      }
      rail.scrollLeft = startScroll - dx;
    };

    const onClickCapture = (event: MouseEvent) => {
      if (!suppressClick) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };

    rail.addEventListener('scroll', onScroll, { passive: true });
    rail.addEventListener('pointerdown', onPointerDown);
    rail.addEventListener('pointermove', onPointerMove);
    rail.addEventListener('pointerup', endDrag);
    rail.addEventListener('pointercancel', endDrag);
    rail.addEventListener('click', onClickCapture, true);
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncRail()) : null;
    ro?.observe(rail);
    return () => {
      rail.removeEventListener('scroll', onScroll);
      rail.removeEventListener('pointerdown', onPointerDown);
      rail.removeEventListener('pointermove', onPointerMove);
      rail.removeEventListener('pointerup', endDrag);
      rail.removeEventListener('pointercancel', endDrag);
      rail.removeEventListener('click', onClickCapture, true);
      ro?.disconnect();
    };
  }, [editable, syncRail, dishes.length]);

  return (
    <section
      className={['ws-menu-highlight', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      data-ws-hl-promenade=""
      id="menu"
    >
      <div className="ws-menu-highlight__stage">
        <header className="ws-menu-highlight__intro">
          <span className="ws-menu-highlight__accent" aria-hidden />
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-highlight__title"
          >
            {sectionTitle}
          </EditableContent>
          {sectionSubtitle || editable ? (
            <EditableContent
              as="p"
              fieldKey="sectionSubtitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-menu-highlight__subtitle"
            >
              {sectionSubtitle}
            </EditableContent>
          ) : null}
        </header>

        <div
          className="ws-menu-highlight__rail-wrap"
          data-overflow={canOverflow ? 'true' : 'false'}
        >
          <div className="ws-menu-highlight__rail-shell">
            <div
              ref={railRef}
              className="ws-menu-highlight__rail"
              role="list"
              aria-label={sectionTitle || 'Highlights'}
              tabIndex={showMarks ? 0 : undefined}
              onKeyDown={showMarks ? onBrowseKeyDown : undefined}
            >
              {dishes.map(({ item, columnIndex, itemIndex }, railIndex) => {
                const itemActive = isMenuItemActive(
                  target?.kind === 'menu-item' ? target : null,
                  columnIndex,
                  itemIndex,
                );
                const imageUrl = resolveItemImageUrl(block, item, columnIndex, itemIndex);
                const imageFieldKey = `itemImage_${columnIndex}_${itemIndex}`;
                const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 900, 78) : undefined;
                const imageSrcSet = imageUrl
                  ? buildSiteImageSrcSet(imageUrl, [320, 480, 640, 900, 1200], 78)
                  : undefined;
                const isLead = railIndex === 0;

                return (
                  <article
                    key={`${columnIndex}-${itemIndex}-${item.name}`}
                    role="listitem"
                    className={[
                      'ws-menu-highlight__plate',
                      isLead ? 'ws-menu-highlight__plate--lead' : '',
                      itemActive ? 'ws-edit-child--active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={{ '--ws-highlight-i': String(railIndex) } as CSSProperties}
                    {...(editable
                      ? {
                          'data-edit-child': 'menu-item' as const,
                          'data-column-index': columnIndex,
                          'data-item-index': itemIndex,
                        }
                      : {})}
                  >
                    <div className="ws-menu-highlight__media">
                      {imageUrl ? (
                        <EditableMedia
                          fieldKey={imageFieldKey}
                          editable={editable}
                          activeEditTarget={activeEditTarget}
                          className="ws-menu-highlight__media-frame"
                          src={imageSrc}
                          srcSet={imageSrcSet}
                          sizes={SITE_IMAGE_SIZES.menuHighlightPlate}
                          alt={item.name || 'Dish'}
                          width={900}
                          height={1125}
                          loading={railIndex < 2 ? 'eager' : 'lazy'}
                          decoding="async"
                        />
                      ) : (
                        <div className="ws-menu-highlight__media-fallback" aria-hidden>
                          <span className="ws-menu-highlight__media-glyph">
                            {(item.name.trim().charAt(0) || '·').toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="ws-menu-highlight__caption">
                      <div className="ws-menu-highlight__head">
                        <MenuItemName
                          item={item}
                          locale={locale}
                          className="ws-menu-highlight__name"
                          showLegalMarks={false}
                          showOrderCode={false}
                        />
                        <MenuItemDietaryTags
                          item={item}
                          locale={locale}
                          iconsOnly
                          placement="inline"
                        />
                      </div>
                      {item.description ? (
                        <p className="ws-menu-highlight__desc">{item.description}</p>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          {showMarks ? (
            <nav
              className="ws-menu-highlight__marks"
              aria-label={marksNavLabel}
              hidden={!canOverflow}
              onKeyDown={onBrowseKeyDown}
            >
              {dishes.map(({ item, columnIndex, itemIndex }, railIndex) => (
                <button
                  key={`mark-${columnIndex}-${itemIndex}-${item.name}`}
                  type="button"
                  className="ws-menu-highlight__mark"
                  aria-label={goToDishLabel.replace('{n}', String(railIndex + 1))}
                  aria-current={railIndex === activeIndex ? 'true' : undefined}
                  onClick={() => {
                    scrollToIndex(railIndex);
                    railRef.current?.focus({ preventScroll: true });
                  }}
                />
              ))}
            </nav>
          ) : null}
        </div>
      </div>
    </section>
  );
}
